/**
 * Sync-back hook: propose Blueprint card updates from a generated derivative.
 *
 * When a provider edits a generated document (e.g. a clinical profile or
 * parent guide) the document diverges from the Blueprint cards it was built
 * from. `useSyncBack` POSTs
 * `POST /api/documents/{id}/sync-back` to extract the clinical divergences and
 * propose card changes via the standard governed ingestion pipeline. On
 * success the caller opens the {@link ReviewPanel} via `ReviewControlContext`.
 * On a 422 (no divergences, or not a generated document) the plain-language
 * server message is surfaced inline — never a backend id or enum string.
 * Proposed content is PHI; nothing is logged.
 */
import { useCallback, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import type { IngestionRun } from "../types";

/** The result surface returned by {@link useSyncBack}. */
export interface UseSyncBackResult {
  /** True while the sync-back POST is in flight. */
  syncing: boolean;
  /**
   * Plain-language error from the server (e.g. "No clinical divergences
   * found."), or null when the last call succeeded or has not been called.
   */
  syncError: string | null;
  /**
   * POST `/api/documents/{documentId}/sync-back`.
   *
   * Resolves to the {@link IngestionRun} on success (caller should open the
   * review panel), or `null` on any failure (error is set on the result).
   */
  syncBack: () => Promise<IngestionRun | null>;
}

/**
 * Manage a sync-back proposal for one generated document.
 *
 * @param documentId - The generated derivative document to sync back from.
 * @returns The syncing flag, error message, and `syncBack` action.
 */
export function useSyncBack(documentId: string): UseSyncBackResult {
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const syncBack = useCallback(async (): Promise<IngestionRun | null> => {
    setSyncing(true);
    setSyncError(null);
    try {
      const resp = await apiUtils.fetchData<IngestionRun>(
        `/api/documents/${encodeURIComponent(documentId)}/sync-back`,
        "POST",
      );
      if (isApiError(resp)) {
        // Surface the plain-language server message directly — the backend
        // produces human-readable 422 messages (not enum strings or ids).
        // Strip the HTTP-prefix prefix apiUtils prepends ("API error! status: 422 - ")
        // so the UI shows only the clean server message.
        const raw = resp.error ?? "Something went wrong. Please try again.";
        const match = /API error! status: \d+ - (.+)$/s.exec(raw);
        const message = match ? match[1] : raw;
        setSyncError(message);
        logger.devOnly.warn("useSyncBack: sync-back failed");
        return null;
      }
      return resp;
    } finally {
      setSyncing(false);
    }
  }, [documentId]);

  return { syncing, syncError, syncBack };
}
